import React from "react";
import PageHero, { FeatureList } from "@/components/PageHero";
import { CTAFooter } from "@/pages/Prestations";
import { CatalogPackages } from "@/components/CatalogPackages";
import NearbyCitiesLinks from "@/components/NearbyCitiesLinks";
import PhotoGallery from "@/components/PhotoGallery";
import { useSeo, JsonLd } from "@/lib/seo";
import { Cake, PartyPopper, Users, Sparkles, PartyPopper as ConfettiIcon, Flame, Crown, Gem } from "lucide-react";

const ANNIV_PHOTOS = Array.from({ length: 8 }, (_, i) => `/assets/anniversaires/anniversaire-${String(i + 1).padStart(2, "0")}.jpg`);

export default function Anniversaire() {
  useSeo({
    title: "DJ anniversaire Nord & Hauts-de-France | My Animation",
    description:
      "Animation DJ pour anniversaire, soirée privée, départ en retraite et fête familiale dans le Nord, le Pas-de-Calais et les Hauts-de-France.",
    path: "/anniversaire",
    keywords: "DJ anniversaire Nord, DJ anniversaire Lille, DJ anniversaire 30 ans, DJ anniversaire 40 ans, DJ anniversaire 50 ans, DJ fête privée",
  });

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "DJ Anniversaire",
    provider: { "@type": "LocalBusiness", name: "My Animation" },
    areaServed: "Hauts-de-France, Nord, Pas-de-Calais, Belgique",
    description: "Animation DJ pour anniversaire, soirée privée, départ en retraite et fête familiale.",
  };

  return (
    <>
      <JsonLd data={serviceLd} />
      <PageHero
        eyebrow="DJ Anniversaire"
        title={<>Une fête <span className="italic gold-text">à votre image.</span></>}
        subtitle="Animation DJ pour anniversaires, fêtes familiales, soirées privées, départs en retraite et événements personnalisés. Playlist adaptée à l'âge, aux invités et à l'ambiance souhaitée — à Lille, Tourcoing, Roubaix, Armentières, Wervicq-Sud, Comines, Halluin, Roncq et dans tout le Nord."
        img={ANNIV_PHOTOS[3] || "/assets/anniversaires/anniversaire-04.jpg"}
      />

      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5 reveal">
            <span className="eyebrow">Pour qui ?</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.02]">
              30, 40, 50 ans<br/><span className="italic gold-text">ou plus.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 reveal">
            <p className="text-white/70 text-base md:text-lg leading-relaxed">
              Que vous souhaitiez fêter un anniversaire intime ou rassembler tous
              vos proches autour d'un événement marquant, My Animation s'adapte à
              votre univers et à votre énergie. Soirée à thème, départ en retraite,
              fête surprise, animation famille : la playlist et le matériel sont
              calibrés pour votre événement.
            </p>
            <div className="mt-10">
              <FeatureList items={[
                "Playlist personnalisée par tranche d'âge",
                "Animation des temps forts",
                "Sonorisation & micros inclus",
                "Mise en lumière disponible",
                "Photobooth en option (dès 179 €)",
                "Karaoké & blind test en option",
                "Effets scéniques selon l'événement",
                "Devis personnalisé sous 24h",
              ]} />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 bg-[#080808] border-y border-[#DCB457]/15">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-14 reveal">
            <span className="eyebrow">Idées d'animation</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3">Tout pour <span className="italic gold-text">marquer l'instant.</span></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { i: Cake, t: "Anniversaire surprise", d: "Mise en scène discrète, moment ovation, slow personnalisé." },
              { i: PartyPopper, t: "Soirée à thème", d: "Années 80, disco, latino, rétro… une playlist sur-mesure." },
              { i: Users, t: "Départ en retraite", d: "Hommage musical, vidéo souvenir, ambiance conviviale." },
              { i: Sparkles, t: "Fête familiale", d: "Animation tous âges, jeux musicaux, blind test famille." },
            ].map(({ i: Icon, t, d }) => (
              <div key={t} className="card-luxe p-7 reveal">
                <Icon size={28} strokeWidth={1} className="text-[#DCB457] mb-5" />
                <h3 className="font-serif-luxe text-2xl">{t}</h3>
                <p className="text-white/55 mt-2 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CatalogPackages
        category="anniversaire"
        title="Nos forfaits anniversaire"
        subtitle={(n) => (<>{n || "Nos"} formule{n > 1 ? "s" : ""}, <span className="italic gold-text">clé en main.</span></>)}
        icons={[ConfettiIcon, Flame, Crown, Gem]}
        testidPrefix="anniv-pack-"
        featuredBadgeLabel="Le plus choisi"
        ctaLabel="Pré-réserver ce forfait"
        footerNote="Tarifs TTC · Hors options spécifiques · Devis personnalisé disponible"
      />

      <PhotoGallery
        images={ANNIV_PHOTOS}
        eyebrow="Nos réalisations"
        title={<>Souvenirs d'<span className="italic gold-text">anniversaires animés.</span></>}
        subtitle="Quelques moments capturés lors d'anniversaires animés par My Animation dans les Hauts-de-France."
        testIdPrefix="anniv-gallery"
      />

      <NearbyCitiesLinks
        eyebrow="Votre DJ anniversaire près de chez vous"
        title={<>DJ anniversaire <span className="italic gold-text">dans votre ville.</span></>}
        urlPrefix="dj-anniversaire"
        intro="My Animation intervient pour vos anniversaires et soirées privées dans tout le Nord, le Pas-de-Calais et les Hauts-de-France. Retrouvez nos pages dédiées ville par ville."
        citySlugs={["lille", "tourcoing", "roubaix", "armentieres", "dunkerque", "lens", "arras", "valenciennes", "cambrai", "bethune"]}
        testidPrefix="anniv-nearby"
      />

      <CTAFooter />
    </>
  );
}
